import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

function mapErrorToSpanish(message) {
  if (!message) {
    return 'No se pudo actualizar la contraseña. Intenta de nuevo.';
  }
  const lower = message.toLowerCase();
  if (lower.includes('same password')) {
    return 'La nueva contraseña debe ser distinta a la anterior.';
  }
  if (
    lower.includes('password') &&
    (lower.includes('least') || lower.includes('short') || lower.includes('long'))
  ) {
    return 'La contraseña no cumple los requisitos mínimos.';
  }
  if (lower.includes('session') || lower.includes('jwt')) {
    return 'Tu sesión expiró. Solicita un nuevo enlace de recuperación.';
  }
  return message;
}

export function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [recoveryReady, setRecoveryReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!alive || !session) return;
        setRecoveryReady(true);
        setVerifying(false);
      }
    );

    (async () => {
      for (let i = 0; i < 12 && alive; i++) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          if (!alive) return;
          setRecoveryReady(true);
          setVerifying(false);
          subscription.unsubscribe();
          return;
        }
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!alive) return;
      const { data: { session } } = await supabase.auth.getSession();
      setRecoveryReady(!!session);
      setVerifying(false);
      subscription.unsubscribe();
    })();

    return () => {
      alive = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (password !== confirmPassword) {
      setFormError('Las contraseñas no coinciden.');
      return;
    }
    if (password.length < 6) {
      setFormError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setFormError(mapErrorToSpanish(error.message));
      return;
    }
    setSuccess(true);
    setTimeout(() => {
      navigate('/dashboard');
    }, 2000);
  };

  return (
    <div className="max-w-md mx-auto mt-12">
      <Card className="p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 text-brand-red mb-4">
            <SafeIcon icon={FiIcons.FiLock} className="text-2xl" />
          </div>
          <h2 className="text-2xl font-bold text-brand-dark">
            Restablecer contraseña
          </h2>
          <p className="text-brand-slate mt-2">
            Elige una nueva contraseña para tu cuenta.
          </p>
        </div>

        {verifying && (
          <div className="text-center text-gray-500 text-sm py-4">
            Verificando enlace…
          </div>
        )}

        {!verifying && !recoveryReady && (
          <div className="space-y-4">
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              Este enlace de recuperación no es válido o ha expirado. Solicita uno
              nuevo desde el inicio de sesión.
            </div>
            <div className="text-center">
              <Link
                to="/login"
                className="text-brand-red font-medium hover:underline text-sm"
              >
                Volver a iniciar sesión
              </Link>
            </div>
          </div>
        )}

        {success && (
          <div className="text-center space-y-4">
            <p className="text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2 text-sm">
              Contraseña actualizada correctamente
            </p>
            <p className="text-sm text-gray-500">
              Redirigiendo al panel…
            </p>
          </div>
        )}

        {!verifying && recoveryReady && !success && (
          <>
            {formError && (
              <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nueva contraseña
                </label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-brand-red focus:border-brand-red outline-none transition"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar contraseña
                </label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-brand-red focus:border-brand-red outline-none transition"
                  placeholder="••••••••"
                />
              </div>
              <Button type="submit" className="w-full py-2.5 mt-2">
                Guardar contraseña
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-gray-500">
              <Link
                to="/login"
                className="text-brand-red font-medium hover:underline"
              >
                Volver a iniciar sesión
              </Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
